/**
 * Date of Birth Page - DOB collection step
 *
 * Screen for entering date of birth with validation and confirmation drawer.
 * Supports MM/DD/YYYY (US) and DD/MM/YYYY (most other countries) based on payoutCountry.
 * Part of the sequential flow: Calculator → Phone → Name → Address → DOB → Identification → Review → Status
 */

import { t } from "@lingui/core/macro";
import { createFileRoute } from "@tanstack/react-router";
import { ArrowLeft, Calendar, Shield } from "lucide-react";
import { useEffect, useId, useState } from "react";
import { z } from "zod";
import { RampsButton } from "@/components/ramps/Button";
import { ConfirmationDrawer } from "@/components/ramps/ConfirmationDrawer";
import { RampsText } from "@/components/ramps/Text";
import { Progress } from "@/components/ui/progress";
import { useContinue } from "@/hooks/useContinue";
import { useNavigateBack } from "@/hooks/useNavigateBack";
import { useAnalytics } from "@/hooks/usePostHog";
import { toast } from "@/lib/toast";
import { cn } from "@/lib/utils";
import {
	type DateFormat,
	displayToISO,
	formatDateInput,
	formatDisplayDateLong,
	getDateFormat,
	getDatePlaceholder,
	isoToDisplay,
	parseDateParts,
} from "@/utils/date-format";
import { rampsActions, useRampsStore } from "../stores/use-ramps-store";

// Zod schema factory — format-aware validation
const createDateOfBirthSchema = (format: DateFormat) =>
	z.object({
		dateOfBirth: z
			.string()
			.min(1, t`Date of birth is required`)
			.regex(
				/^\d{2}\/\d{2}\/\d{4}$/,
				format === "dd/mm/yyyy"
					? t`Date must be in DD/MM/YYYY format`
					: t`Date must be in MM/DD/YYYY format`,
			)
			.refine((date) => {
				const parts = parseDateParts(date, format);
				if (!parts) return false;

				const { month, day, year } = parts;
				const dateObj = new Date(year, month - 1, day);
				return (
					dateObj.getFullYear() === year &&
					dateObj.getMonth() === month - 1 &&
					dateObj.getDate() === day
				);
			}, t`Please enter a valid date`)
			.refine((date) => {
				const parts = parseDateParts(date, format);
				if (!parts) return false;

				const { month, day, year } = parts;
				const dateObj = new Date(year, month - 1, day);

				const today = new Date();
				const minDate = new Date(
					today.getFullYear() - 18,
					today.getMonth(),
					today.getDate(),
				);

				return dateObj <= minDate;
			}, t`You must be at least 18 years old`)
			.refine((date) => {
				const parts = parseDateParts(date, format);
				if (!parts) return false;

				const minYear = new Date().getFullYear() - 100;
				return parts.year >= minYear;
			}, t`Please enter a valid birth year`),
	});

function DateOfBirthPage() {
	const search = Route.useSearch();
	const store = useRampsStore();
	const handleBack = useNavigateBack();
	const handleContinue = useContinue();
	const { trackPageView } = useAnalytics();

	// Determine date format based on payout country
	const dateFormat = getDateFormat(store.payoutCountry);

	// Track page view
	useEffect(() => {
		trackPageView("date-of-birth", { page_name: "date-of-birth" });
	}, [trackPageView]);

	// Generate unique ID for form field
	const dateOfBirthId = useId();

	// Check if profile data comes from backend (existing user profile)
	const isExistingProfile = store.quoteResponse?.isExistingProfile ?? false;

	// State variables
	const [dateOfBirth, setDateOfBirth] = useState(() => {
		if (store.dateOfBirth) {
			return isoToDisplay(store.dateOfBirth, dateFormat);
		}
		return "";
	});
	const [showConfirmationDrawer, setShowConfirmationDrawer] = useState(false);

	// Token and transaction_id are synced globally by URLParamsInitializer

	const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const formatted = formatDateInput(e.target.value, dateFormat);
		setDateOfBirth(formatted);
	};

	const validateAndShowConfirmation = () => {
		try {
			createDateOfBirthSchema(dateFormat).parse({ dateOfBirth });
			setShowConfirmationDrawer(true);
		} catch (error) {
			if (error instanceof z.ZodError) {
				toast.validationError(error.issues[0].message);
			}
		}
	};

	const handleConfirm = () => {
		const isoDateString = displayToISO(dateOfBirth, dateFormat);

		// Save to store
		rampsActions.setDateOfBirth(isoDateString);
		rampsActions.setCurrentStep("identification");

		handleContinue(search);
	};

	const handleEdit = () => {
		setShowConfirmationDrawer(false);
	};

	return (
		<div className="flex min-h-dvh flex-col bg-ramps-background-base">
			{/* Header */}
			<div className="flex items-center justify-between p-4">
				<button
					type="button"
					onClick={handleBack}
					className="flex h-8 w-8 items-center justify-center transition-opacity hover:opacity-80"
					aria-label="Back"
				>
					<ArrowLeft className="h-6 w-6 text-ramps-text-primary" />
				</button>
			</div>

			{/* Progress bar */}
			<div className="mb-8 px-4">
				<Progress
					value={70}
					className="h-1 bg-ramps-background-surface-2 *:bg-ramps-text-primary"
				/>
			</div>

			{/* Content */}
			<div className="flex flex-1 flex-col px-4">
				<div className="mb-8 text-left">
					<RampsText variant="title-lg-bold" color="primary" className="mb-2">
						{t`Your date of birth`}
					</RampsText>
				</div>

				{/* Form */}
				<div className="flex-1 space-y-6">
					<div className="space-y-4">
						{/* Date of Birth Input */}
						<div className="relative rounded-lg bg-ramps-background-surface-2 px-4">
							{/* Floating Label - only show when user has typed */}
							<div className="pointer-events-none absolute top-2 left-4">
								<RampsText
									variant="body-sm"
									color="secondary"
									className={cn(
										"transition-all duration-200 ease-in-out",
										dateOfBirth ? "opacity-100" : "opacity-0",
									)}
								>
									{t`Date of birth`}
								</RampsText>
							</div>

							{/* Input Field */}
							<input
								id={dateOfBirthId}
								type="text"
								inputMode="numeric"
								placeholder={getDatePlaceholder(dateFormat)}
								value={dateOfBirth}
								disabled={isExistingProfile}
								onChange={handleDateChange}
								maxLength={10}
								className={cn(
									"w-full border-none bg-transparent p-0 font-medium text-lg text-ramps-text-primary placeholder:text-ramps-text-disabled focus:outline-none focus-visible:ring-0",
									// Dynamic alignment and padding: centered when empty, left-aligned with top padding when has content
									dateOfBirth ? "pt-6 pb-2 text-left" : "py-4 text-left",
								)}
							/>
						</div>

						{/* Information notice */}
						<div className="mt-6 rounded-lg border border-ramps-primary bg-transparent p-4">
							<div className="flex items-center gap-3">
								<Shield className="mt-0.5 h-6 w-6 shrink-0 text-ramps-text-primary" />
								<RampsText
									variant="body-sm"
									color="primary"
									className="leading-relaxed"
								>
									{t`We use this to help prevent fraud. Your info is always kept private.`}
								</RampsText>
							</div>
						</div>
					</div>
				</div>

				{/* Continue Button */}
				<div className="pt-8 pb-6">
					<RampsButton
						onClick={validateAndShowConfirmation}
						disabled={!dateOfBirth || dateOfBirth.length !== 10}
						className="h-12 w-full rounded-lg bg-black font-medium text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
					>
						{t`Continue`}
					</RampsButton>
				</div>
			</div>

			{/* Date of Birth Confirmation Drawer.
			    Shares ConfirmationDrawer with the name step so the scrollable
			    body / pinned footer layout stays consistent — this sheet used to
			    inline its own copy and trapped the Confirm button off-screen on
			    short viewports and longer translations (RAMPS-4427). */}
			<ConfirmationDrawer
				open={showConfirmationDrawer}
				onOpenChange={setShowConfirmationDrawer}
				srTitle={t`Date of Birth Confirmation`}
				icon={Calendar}
				title={t`Is this your date of birth?`}
				description={t`Make sure your birthday matches what's on your legal ID to avoid money transfer delays.`}
				value={formatDisplayDateLong(dateOfBirth, dateFormat)}
				confirmLabel={t`Confirm`}
				onConfirm={handleConfirm}
				onEdit={handleEdit}
			/>
		</div>
	);
}

export const Route = createFileRoute("/ramps/date-of-birth")({
	validateSearch: (search) =>
		search as {
			step?: string;
			transaction_id?: string;
			token?: string;
			accesstoken?: string;
		},
	component: DateOfBirthPage,
});
